type TEmbedWindow = Window & {
    __DTRADER_IS_EMBEDDED?: boolean;
    __DTRADER_EMBED_PARENT_ORIGIN?: string;
};

/** True when DTrader runs inside a parent iframe (dbotspace / money8gg-style embed). */
export const isDTraderEmbedded = (): boolean =>
    typeof window !== 'undefined' && (window as TEmbedWindow).__DTRADER_IS_EMBEDDED === true;

const embedPostTarget = (): string => {
    if (typeof window === 'undefined') return '*';
    return (window as TEmbedWindow).__DTRADER_EMBED_PARENT_ORIGIN || '*';
};

export type TDTraderEmbedTradeParameters = Record<string, unknown>;

export type TDTraderEmbedTradePreparePayload = {
    replication_id: string;
    contract_type: string;
    buy_price: number | string;
    parameters: TDTraderEmbedTradeParameters;
};

export type TDTraderEmbedTradePlacedPayload = {
    replication_id?: string;
    contract_id: number | string;
    buy_price: number | string;
    currency: string;
    contract_type: string;
    symbol: string;
    transaction_id?: number | string;
    /** Proposal fields (amount, duration, barriers, etc.) for parent copy-trading replication. */
    parameters: TDTraderEmbedTradeParameters;
};

/** Fired when the user clicks buy — parent can prepare follower proposals in parallel with the master buy. */
export const notifyParentTradePrepare = (payload: TDTraderEmbedTradePreparePayload): void => {
    if (!isDTraderEmbedded() || typeof window === 'undefined' || !window.parent) return;

    window.parent.postMessage(
        {
            type: 'TRADE_PREPARE',
            source: 'dtrader-embed',
            ...payload,
        },
        embedPostTarget()
    );
};

/** Notifies the parent app that a contract was purchased (for copy-trading mirroring). */
export const notifyParentTradePlaced = (payload: TDTraderEmbedTradePlacedPayload): void => {
    if (!isDTraderEmbedded() || typeof window === 'undefined' || !window.parent) return;

    window.parent.postMessage(
        {
            type: 'TRADE_PLACED',
            source: 'dtrader-embed',
            ...payload,
        },
        embedPostTarget()
    );
};

export type TDTraderEmbedContractSoldPayload = {
    contract_id: number | string;
    sell_price?: number | string;
};

/** Notifies the parent app that a contract was sold early (mirror sell on follower accounts). */
export const notifyParentContractSold = (payload: TDTraderEmbedContractSoldPayload): void => {
    if (!isDTraderEmbedded() || typeof window === 'undefined' || !window.parent) return;

    window.parent.postMessage(
        {
            type: 'CONTRACT_SOLD',
            source: 'dtrader-embed',
            ...payload,
        },
        embedPostTarget()
    );
};
